import { Box, Skeleton } from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { convertOpenFlowToReactFlow } from '../../utils/OpenFlowToReactFlow';
import {
  ReactFlow,
  Background,
  Controls,
  ReactFlowProvider,
  Node,
  Edge,
  OnNodesChange,
  applyNodeChanges,
  OnEdgesChange,
  applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getNodeTypes } from '../../components/Windmill/CustomNodes';
import { getEdgeTypes } from '../../components/Windmill/CustomEdges';
import JobDetailDrawer from './JobDetailDrawer';
import { getWindmillJobDetailsApi } from '../../utils/apis';

interface JobDetailAreaProps {
  job: any;
  onBack: () => void;
  isJobDetailsLoading: boolean;
}

const JobDetailAreaContent: React.FC<JobDetailAreaProps> = ({
  job,
  isJobDetailsLoading,
}) => {
  const [aggregatedJob, setAggregatedJob] = useState<any>(null);
  const [isAggregating, setIsAggregating] = useState(true);

  useEffect(() => {
    const fetchAndAggregateFlowData = async (initialJob: any) => {
      if (!initialJob) return;

      setIsAggregating(true);
      const newAggregatedJob = JSON.parse(JSON.stringify(initialJob));
      const jobsToFetch = new Set<string>();
      const processedJobs = new Set<string>([initialJob.id]);

      // Initialize aggregated failure modules array
      if (!newAggregatedJob.flow_status.aggregated_failure_modules) {
        newAggregatedJob.flow_status.aggregated_failure_modules = [];
      }

      // Initial scan of top-level modules
      initialJob.flow_status?.modules?.forEach((module: any) => {
        if (module.job && module.id.includes('branch')) {
          jobsToFetch.add(module.job);
        }
      });

      while (jobsToFetch.size > 0) {
        const [jobId] = jobsToFetch;
        jobsToFetch.delete(jobId);

        if (processedJobs.has(jobId)) continue;

        try {
          const response = await getWindmillJobDetailsApi(jobId);
          const nestedJob = response.data;
          processedJobs.add(jobId);

          if (nestedJob?.flow_status?.modules) {
            newAggregatedJob.flow_status.modules.push(
              ...nestedJob.flow_status.modules
            );

            // Scan the new modules for more nested jobs
            nestedJob.flow_status.modules.forEach((module: any) => {
              if (module.job && module.id.includes('branch')) {
                if (!processedJobs.has(module.job)) {
                  jobsToFetch.add(module.job);
                }
              }
            });
          }

          // NEW: Collect failure modules that have job and parent_module data
          if (nestedJob?.flow_status?.failure_module) {
            const failureModule = nestedJob.flow_status.failure_module;
            // Only collect failure modules that have meaningful data (job and parent_module)
            if (failureModule.job && failureModule.parent_module) {
              newAggregatedJob.flow_status.aggregated_failure_modules.push({
                ...failureModule,
                // Add context about which nested job this failure module came from
                source_job_id: jobId,
              });
            }
          }
        } catch (error) {
          console.error(`Failed to fetch nested job ${jobId}:`, error);
        }
      }

      setAggregatedJob(newAggregatedJob);
      setIsAggregating(false);
    };

    fetchAndAggregateFlowData(job);
  }, [job]);

  // Move all hooks before any conditional returns
  const flowData = useMemo(() => {
    if (!aggregatedJob) return null;
    return convertOpenFlowToReactFlow(aggregatedJob, job, true);
  }, [aggregatedJob, job]);

  // State for nodes and edges with dragging support
  const [nodes, setNodes] = useState<Node[]>(flowData?.nodes || []);
  const [edges, setEdges] = useState<Edge[]>(flowData?.edges || []);
  const [open, setOpen] = useState(false);
  const [nodeData, setNodeData] = useState<any>(null);

  useEffect(() => {
    if (flowData) {
      setNodes(flowData.nodes || []);
      setEdges(flowData.edges || []);
    }
  }, [flowData]);

  const nodeTypes = useMemo(() => getNodeTypes(), []);
  const edgeTypes = useMemo(() => getEdgeTypes(), []);

  // Handle node changes (dragging, selection, etc.)
  const onNodesChange: OnNodesChange = useCallback(changes => {
    setNodes(nds => applyNodeChanges(changes, nds));
  }, []);

  // Handle edge changes
  const onEdgesChange: OnEdgesChange = useCallback(changes => {
    setEdges(eds => applyEdgeChanges(changes, eds));
  }, []);

  const handleNodeClick = useCallback((event: any, node: any) => {
    console.log('event', event);
    setOpen(true);
    setNodeData(node.data);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  // Now we can have conditional returns
  if (isJobDetailsLoading || isAggregating) {
    return (
      <Box sx={{ height: '100vh', width: '100%', position: 'relative' }}>
        <Skeleton variant="rectangular" height="100%" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 180px)',
        width: '100%',
        position: 'relative',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        fitView
        style={{ backgroundColor: '#f8f9fa' }}
        proOptions={{ hideAttribution: true }}
        panOnScroll={true}
        panOnScrollSpeed={1}
        panOnDrag={false} // Disable pan on drag to allow node dragging
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        defaultEdgeOptions={{
          type: 'custom',
        }}
        nodesDraggable={true} // Enable node dragging
        nodesConnectable={true}
        elementsSelectable={true}
        selectNodesOnDrag={false} // Prevent selection when dragging
        multiSelectionKeyCode="Shift" // Hold Shift for multi-selection
        deleteKeyCode="Delete" // Delete key to remove selected elements
      >
        <Background color="#e0e0e0" gap={10} />
        <Controls />
      </ReactFlow>
      <JobDetailDrawer open={open} onClose={handleClose} nodeData={nodeData} />
    </Box>
  );
};

const JobDetailArea: React.FC<JobDetailAreaProps> = ({
  job,
  onBack,
  isJobDetailsLoading,
}) => {
  return (
    <ReactFlowProvider>
      <JobDetailAreaContent
        job={job}
        onBack={onBack}
        isJobDetailsLoading={isJobDetailsLoading}
      />
    </ReactFlowProvider>
  );
};

export default JobDetailArea;
